import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  const services = [
    'Custom Website Development',
    'Web Application Development',
    'E-Commerce Solutions',
    'Frontend Development',
    'Backend Development',
    'API Integration',
    'CMS & Admin Panels',
    'Website Redesign',
    'Performance Optimization',
    'Website Maintenance & Support'
  ];

  const frontendTech = ['HTML5', 'CSS3', 'JavaScript', 'Bootstrap', 'Tailwind CSS'];
  const backendTech = ['Node.js', 'Express.js', 'PHP'];
  const databases = ['MongoDB', 'MySQL'];

  const developmentFocus = [
    'Responsive Design',
    'SEO-Friendly Structure',
    'Fast Performance',
    'Scalable Architecture',
    'Security Optimization',
    'Clean Code Practices',
    'Cross-Browser Compatibility'
  ];

  const whyChooseUs = [
    {
      title: 'Modern & Responsive Design',
      description: 'We create visually modern and mobile-friendly interfaces optimized for all screen sizes.'
    },
    {
      title: 'Performance-Oriented Development',
      description: 'Fast-loading websites and optimized applications built for smooth user experience.'
    },
    {
      title: 'Custom-Built Solutions',
      description: 'Every project is designed according to business goals and specific operational needs.'
    },
    {
      title: 'Scalable Architecture',
      description: 'Systems built to support future growth and expansion requirements.'
    },
    {
      title: 'Reliable Communication',
      description: 'Transparent workflow, timely updates, and continuous collaboration throughout development.'
    },
    {
      title: 'Ongoing Support',
      description: 'Post-launch maintenance, improvements, and technical support for long-term reliability.'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Requirement Discussion',
      description: 'Understanding business goals, target audience, and project requirements.'
    },
    {
      step: '02',
      title: 'Planning & Strategy',
      description: 'Structuring the website/application architecture and user experience.'
    },
    {
      step: '03',
      title: 'UI/UX Design',
      description: 'Creating clean, modern, and responsive interface designs.'
    },
    {
      step: '04',
      title: 'Development',
      description: 'Building frontend, backend, APIs, and system functionality.'
    },
    {
      step: '05',
      title: 'Testing & Optimization',
      description: 'Performance testing, responsiveness checks, and bug fixing.'
    },
    {
      step: '06',
      title: 'Deployment & Support',
      description: 'Launching the project and providing continuous support and maintenance.'
    }
  ];

  const stats = [
    { number: '10+', label: 'Projects Completed' },
    { number: '100%', label: 'Responsive Solutions' },
    { number: '15+', label: 'Technologies Used' },
    { number: '99%', label: 'Client Satisfaction' }
  ];

  const faqs = [
    {
      question: 'What services do you offer?',
      answer: 'We offer complete web development solutions including custom websites, web applications, e-commerce platforms, API integrations, and ongoing maintenance.'
    },
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on complexity. Simple websites take 2-4 weeks, while complex applications may take 8-12 weeks or more.'
    },
    {
      question: 'Do you provide ongoing support?',
      answer: 'Yes, we offer comprehensive post-launch support including maintenance, updates, security patches, and performance optimization.'
    },
    {
      question: 'What technologies do you work with?',
      answer: 'We work with modern technologies including React, Node.js, PHP, MongoDB, MySQL, and various frontend frameworks for optimal performance.'
    },
    {
      question: 'Do you handle hosting and domain setup?',
      answer: 'We can assist with hosting recommendations and domain setup, or work with your preferred hosting provider for seamless deployment.'
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>About Wevatar</h1>
          <p className="hero-subtitle">
            Building modern websites, scalable web applications, and custom digital solutions designed for performance, usability, and business growth.
          </p>
          <div className="hero-buttons">
            <Link to="/portfolio" className="btn-primary">View Portfolio</Link>
            <Link to="/contact" className="btn-secondary">Contact Us</Link>
          </div>
        </motion.div>
        <div className="hero-bg">
          <div className="floating-elements">
            <div className="floating-orb orb-1"></div>
            <div className="floating-orb orb-2"></div>
            <div className="floating-orb orb-3"></div>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="company-intro">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Who We Are</h2>
          <div className="intro-content">
            <p>
              At Wevatar, we specialize in creating modern, scalable, and performance-focused digital solutions for businesses, startups, organizations, and growing brands. Our goal is to help businesses establish a strong online presence through professionally designed websites and reliable web applications.
            </p>
            <p>
              We focus on delivering clean design, optimized performance, responsive user experiences, and scalable backend systems tailored to real business requirements. From business websites and portfolio platforms to custom dashboards and dynamic management systems, we build solutions that combine functionality with modern digital aesthetics.
            </p>
            <p>
              Our development approach prioritizes quality, usability, speed, and long-term scalability to ensure every project performs efficiently across devices and platforms.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision">
        <div className="container">
          <div className="mission-vision-grid">
            <motion.div
              className="mission-card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3>Our Mission</h3>
              <p>
                To deliver reliable, modern, and scalable web solutions that help businesses grow digitally through high-quality development, responsive design, and performance-driven technology.
              </p>
            </motion.div>
            <motion.div
              className="vision-card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Our Vision</h3>
              <p>
                To become a trusted digital solutions brand known for building innovative, user-focused, and future-ready web experiences for businesses worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="what-we-do">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>What We Do</h2>
          <p className="section-subtitle">
            We provide end-to-end web development solutions designed to help businesses establish, improve, and scale their digital presence.
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <span className="service-icon">⚡</span>
                <span>{service}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Technologies */}
      <section className="technologies">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Technologies We Work With</h2>
          <p className="section-subtitle">
            We use modern technologies and development practices to build secure, scalable, and efficient digital products.
          </p>

          <div className="tech-sections">
            <div className="tech-section">
              <h3>Frontend</h3>
              <div className="tech-grid">
                {frontendTech.map((tech, index) => (
                  <span key={index} className="tech-badge">{tech}</span>
                ))}
              </div>
            </div>

            <div className="tech-section">
              <h3>Backend</h3>
              <div className="tech-grid">
                {backendTech.map((tech, index) => (
                  <span key={index} className="tech-badge">{tech}</span>
                ))}
              </div>
            </div>

            <div className="tech-section">
              <h3>Databases</h3>
              <div className="tech-grid">
                {databases.map((tech, index) => (
                  <span key={index} className="tech-badge">{tech}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="development-focus">
            <h3>Development Focus</h3>
            <div className="focus-grid">
              {developmentFocus.map((focus, index) => (
                <motion.div
                  key={index}
                  className="focus-item"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <span className="focus-icon">✓</span>
                  <span>{focus}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-us">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Why Choose Wevatar</h2>
          <div className="why-choose-grid">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                className="why-choose-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Development Process */}
      <section className="development-process">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Our Development Process</h2>
          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                className="process-step"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Statistics */}
      <section className="stats-section">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Frequently Asked Questions</h2>
          <p className="section-subtitle">Got Questions? We've Got Clear Answers.</p>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="faq-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <details>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Let's Build Something Modern Together</h2>
          <p>
            Whether you need a professional business website, a scalable web application, or a complete digital solution, Wevatar is ready to help transform your ideas into modern digital experiences.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Contact Us</Link>
            <Link to="/portfolio" className="btn-secondary">View Portfolio</Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default About;